import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import { Menu, X, Leaf, ChevronDown, ShoppingCart, Heart, User, LogOut, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

export const Navbar = () => {
  const { t, language, setLanguage, languages, getLocalizedText } = useLanguage();
  const { isAuthenticated: isAdminAuthenticated } = useAuth();
  const { user, isAuthenticated: isUserAuthenticated, isAdmin: isUserAdmin, logout: userLogout } = useUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // User is admin if logged in via AuthContext OR via UserContext with is_admin flag
  const canAccessAdmin = isAdminAuthenticated || isUserAdmin;

  const shoppingListLabel = getLocalizedText({ fi: 'Kauppalista', fr: 'Liste des courses', en: 'Shopping List' });
  const favoritesLabel = getLocalizedText({ fi: 'Suosikit', fr: 'Favoris', en: 'Favorites' });
  const mealPlannerLabel = getLocalizedText({ fi: 'Viikkosuunnitelma', fr: 'Planning', en: 'Meal Planner' });

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/recipes', label: t('nav.recipes') },
    { path: '/health', label: t('nav.health') },
    { path: '/why-vegan', label: t('nav.whyVegan') },
    { path: '/techniques', label: t('nav.techniques') },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const currentLanguage = languages.find(l => l.code === language);

  return (
    <nav className="glass-nav sticky top-0 z-50 border-b border-white/20" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <Leaf className="h-8 w-8 text-primary transition-transform group-hover:rotate-12" />
            <span className="text-xl md:text-2xl font-bold text-primary font-serif">
              love4vegan
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  isActive(link.path) 
                    ? 'text-primary border-b-2 border-primary pb-1' 
                    : 'text-foreground/70'
                }`}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side: Favorites + Shopping List + User + Language + Admin */}
          <div className="hidden md:flex items-center gap-2">
            {/* Favorites */}
            <Link to="/favorites">
              <Button 
                variant="ghost" 
                size="icon"
                className="relative"
                data-testid="favorites-nav-btn"
                title={favoritesLabel}
              >
                <Heart className="h-5 w-5" />
              </Button>
            </Link>

            {/* Meal Planner */}
            <Link to="/meal-planner">
              <Button 
                variant="ghost" 
                size="icon"
                className="relative"
                data-testid="meal-planner-nav-btn"
                title={mealPlannerLabel}
              >
                <Calendar className="h-5 w-5" />
              </Button>
            </Link>

            {/* Shopping List */}
            <Link to="/shopping-list">
              <Button 
                variant="ghost" 
                size="icon"
                className="relative"
                data-testid="shopping-list-nav-btn"
                title={shoppingListLabel}
              >
                <ShoppingCart className="h-5 w-5" />
              </Button>
            </Link>

            {/* User Menu */}
            {isUserAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 px-3"
                    data-testid="user-menu-btn"
                  >
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm max-w-[100px] truncate">{user?.name}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/favorites" className="flex items-center gap-2 cursor-pointer">
                      <Heart className="h-4 w-4" />
                      {favoritesLabel}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/meal-planner" className="flex items-center gap-2 cursor-pointer">
                      <Calendar className="h-4 w-4" />
                      {mealPlannerLabel}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={userLogout}
                    className="flex items-center gap-2 cursor-pointer text-destructive"
                    data-testid="user-logout-btn"
                  >
                    <LogOut className="h-4 w-4" />
                    {getLocalizedText({ fi: 'Kirjaudu ulos', fr: 'Déconnexion', en: 'Log out' })}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth">
                <Button 
                  variant="ghost"
                  size="sm"
                  className="text-foreground/70 hover:text-foreground"
                  data-testid="login-btn"
                >
                  <User className="h-4 w-4 mr-2" />
                  {getLocalizedText({ fi: 'Kirjaudu', fr: 'Connexion', en: 'Sign In' })}
                </Button>
              </Link>
            )}

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 px-3"
                  data-testid="language-selector"
                >
                  <span className="text-lg">{currentLanguage?.flag}</span>
                  <span className="text-sm">{currentLanguage?.code.toUpperCase()}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`flex items-center gap-2 cursor-pointer ${
                      language === lang.code ? 'bg-accent' : ''
                    }`}
                    data-testid={`language-option-${lang.code}`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span>{lang.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Admin Link - only show if user is admin */}
            {canAccessAdmin && (
              <Link to="/admin">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  data-testid="admin-button"
                >
                  {t('nav.admin')}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-foreground" />
            ) : (
              <Menu className="h-6 w-6 text-foreground" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/50 animate-fadeIn" data-testid="mobile-menu">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-primary/10 text-primary'
                      : 'text-foreground/70 hover:bg-muted/20'
                  }`}
                  data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}

              {/* Favorites Mobile */}
              <Link
                to="/favorites"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  isActive('/favorites')
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground/70 hover:bg-muted/20'
                }`}
                data-testid="mobile-nav-link-favorites"
              >
                <Heart className="h-4 w-4" />
                {favoritesLabel}
              </Link>

              {/* Shopping List Mobile */}
              <Link
                to="/shopping-list"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  isActive('/shopping-list')
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground/70 hover:bg-muted/20'
                }`}
                data-testid="mobile-nav-link-shopping-list"
              >
                <ShoppingCart className="h-4 w-4" />
                {shoppingListLabel}
              </Link>

              {/* User Auth Mobile */}
              {isUserAuthenticated ? (
                <button
                  onClick={() => { userLogout(); setMobileMenuOpen(false); }}
                  className="px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 text-foreground/70 hover:bg-muted/20"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                  {getLocalizedText({ fi: 'Kirjaudu ulos', fr: 'Déconnexion', en: 'Log out' })} ({user?.name})
                </button>
              ) : (
                <Link
                  to="/auth"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 text-foreground/70 hover:bg-muted/20"
                  data-testid="mobile-login-btn"
                >
                  <User className="h-4 w-4" />
                  {getLocalizedText({ fi: 'Kirjaudu', fr: 'Connexion', en: 'Sign In' })}
                </Link>
              )}
              
              {/* Mobile Language Selector */}
              <div className="px-4 py-3 flex items-center gap-2">
                <span className="text-sm text-foreground/70">Language:</span>
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`px-2 py-1 rounded text-lg ${
                      language === lang.code ? 'bg-accent' : ''
                    }`}
                    data-testid={`mobile-language-${lang.code}`}
                  >
                    {lang.flag}
                  </button>
                ))}
              </div>

              {/* Mobile Admin Link - only show if user is admin */}
              {canAccessAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="mx-4 mt-2"
                >
                  <Button 
                    variant="outline" 
                    className="w-full border-primary text-primary"
                    data-testid="mobile-admin-button"
                  >
                    {t('nav.admin')}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
